/**
 * Attendance report view (Phase 3) — a unified roster over the backend-owned
 * attendance resolver. Summary cards + cohort filter + paginated roster + complete
 * CSV download + no-show follow-up-segment materialization. Badges use authoritative
 * backend totals (never the loaded-page length); realtime on event_attendees keeps
 * them fresh. All numbers come from the SECURITY DEFINER RPCs, which enforce event
 * authorization server-side.
 */
import { useCallback, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { supabase } from "../../services/supabase";
import {
  type AttendanceCohort,
  type AttendanceRosterRow,
  downloadAttendanceCsv,
  getAttendanceRosterPage,
  getAttendanceSummary,
  materializeNoshowList,
  type MaterializeNoshowResult,
} from "../../services/attendanceReport.service";
import Button from "../common/Button";
import LoadingSpinner from "../common/LoadingSpinner";
import { useToast } from "../ui/Toast";
import { formatDate, formatTime } from "../../utils/formatters";
import {
  canonicalAttendance,
  canonicalRowAttendance,
  canonicalRowRegistered,
} from "../../utils/eventAttendance";
import type { Event } from "../../types/event.types";

const COHORT_TABS: { key: AttendanceCohort; label: string }[] = [
  { key: "all", label: "All" },
  { key: "checked_in", label: "Checked in" },
  { key: "awaiting_checkin", label: "Awaiting" },
  { key: "no_show", label: "No-shows" },
  { key: "walk_in", label: "Walk-ins" },
  { key: "cancelled", label: "Cancelled" },
];

function fmtDateTime(ts: string | null): string {
  if (!ts) {
    return "—";
  }
  return `${formatDate(ts)} ${formatTime(ts)}`;
}

const OUTCOME_LABEL: Record<string, string> = {
  checked_in: "Checked in",
  awaiting_checkin: "Awaiting check-in",
  no_show: "No-show",
  walk_in: "Walk-in",
  cancelled: "Cancelled",
  attendance_evidence_incomplete: "Attendance evidence incomplete",
  attributed: "Attribution only",
  other: "Other",
};

const OUTCOME_CLASS: Record<string, string> = {
  checked_in: "bg-green-100 text-green-800",
  walk_in: "bg-blue-100 text-blue-800",
  awaiting_checkin: "bg-amber-100 text-amber-800",
  no_show: "bg-red-100 text-red-800",
  cancelled: "bg-gray-100 text-gray-600",
  attendance_evidence_incomplete: "bg-orange-100 text-orange-800",
  attributed: "bg-gray-100 text-gray-500",
  other: "bg-gray-100 text-gray-500",
};

/**
 * Adapt a roster row to the shared canonical arithmetic. The RPC exposes the party columns
 * under `registered_*_count`; everything else is the same shape.
 *
 * The roster's own `attended_headcount` column is the RAW attended_m + attended_n, with NONE
 * of the resolver's rules applied — no GREATEST(...,1) floor and no "verified where known,
 * else the registered headcount" fallback. Rendering it directly is how this table came to
 * print "Attended 0" for a walk-in guest the summary above counts as 1, and "—" for every
 * advance registrant who really did check in (their attended_* columns are NULL).
 */
function rowCounts(r: AttendanceRosterRow) {
  const shaped = {
    attendee_type: r.attendee_type,
    // The roster RPC exposes has_active_subscription as `current_member`; it does not
    // carry contact_type or membership_id, so this surface can only reach the weaker
    // rungs of the "membership id wins" cascade. It affects the member/non-member SPLIT
    // of a floor-supplied body only — never a total.
    has_active_subscription: r.current_member,
    party_member_count: r.registered_member_count,
    party_nonmember_count: r.registered_nonmember_count,
    attended_member_count: r.attended_member_count,
    attended_nonmember_count: r.attended_nonmember_count,
  };
  return {
    registered: canonicalRowRegistered(shaped),
    attended: canonicalRowAttendance(shaped, r.cohort),
  };
}

const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  sub?: string;
}> = ({ label, value, sub }) => (
  <div className="bg-white rounded-lg border border-gray-200 px-4 py-3">
    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
      {label}
    </div>
    <div className="mt-1 text-2xl font-semibold text-gray-900">{value}</div>
    {sub && <div className="text-xs text-gray-500 mt-0.5">{sub}</div>}
  </div>
);

const AttendanceReportView: React.FC<{ eventId: string; event: Event }> = ({
  eventId,
  event,
}) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [cohort, setCohort] = useState<AttendanceCohort>("all");
  const [rows, setRows] = useState<AttendanceRosterRow[]>([]);
  const [cursor, setCursor] = useState<{ sort: string; id: string } | null>(
    null,
  );
  const [rosterLoading, setRosterLoading] = useState(true);
  const [rosterError, setRosterError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [showMaterialize, setShowMaterialize] = useState(false);
  const [materializing, setMaterializing] = useState(false);
  const [materializeResult, setMaterializeResult] =
    useState<MaterializeNoshowResult | null>(null);

  const {
    data: summary,
    isLoading: summaryLoading,
    error: summaryError,
  } = useQuery({
    queryKey: ["attendance-summary", eventId, reloadToken],
    queryFn: () => getAttendanceSummary(eventId),
    retry: false,
  });

  // Load the first page whenever the cohort or reload token changes.
  useEffect(() => {
    let cancelled = false;
    setRosterLoading(true);
    setRosterError(null);
    getAttendanceRosterPage(eventId, cohort, null, 100)
      .then((page) => {
        if (cancelled) {
          return;
        }
        setRows(page.rows);
        setCursor(page.nextCursor);
      })
      .catch((e) => {
        if (cancelled) {
          return;
        }
        setRosterError(
          e instanceof Error ? e.message : "Failed to load the roster.",
        );
      })
      .finally(() => {
        if (!cancelled) {
          setRosterLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [eventId, cohort, reloadToken]);

  const loadMore = useCallback(async () => {
    if (!cursor) {
      return;
    }
    try {
      const page = await getAttendanceRosterPage(eventId, cohort, cursor, 100);
      setRows((prev) => [...prev, ...page.rows]);
      setCursor(page.nextCursor);
    } catch (e) {
      showToast(
        "error",
        e instanceof Error ? e.message : "Failed to load more.",
      );
    }
  }, [eventId, cohort, cursor, showToast]);

  // Realtime: any attendee change (esp. checked_in_at) refreshes totals + the roster.
  useEffect(() => {
    const channel = supabase
      .channel(`attendance-report-${eventId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "event_attendees",
          filter: `event_id=eq.${eventId}`,
        },
        () => {
          queryClient.invalidateQueries({
            queryKey: ["attendance-summary", eventId],
          });
          setReloadToken((t) => t + 1);
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [eventId, queryClient]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadAttendanceCsv(eventId, cohort, event.title);
      showToast("success", "Attendance report downloaded.");
    } catch (e) {
      showToast("error", e instanceof Error ? e.message : "Download failed.");
    } finally {
      setDownloading(false);
    }
  };

  const handleMaterialize = async () => {
    setMaterializing(true);
    try {
      const result = await materializeNoshowList(eventId);
      setMaterializeResult(result);
      setShowMaterialize(false);
      showToast(
        "success",
        `Follow-up segment refreshed: ${result.materialized_contact_count} contacts added.`,
      );
    } catch (e) {
      showToast(
        "error",
        e instanceof Error ? e.message : "Could not refresh the segment.",
      );
    } finally {
      setMaterializing(false);
    }
  };

  // ONE canonical attendance contract, shared with EventDetail and the Overview funnel.
  const canonical = summary ? canonicalAttendance(summary) : null;

  const noShowCount = summary?.no_show_rows ?? 0;
  const cutoffPassed = noShowCount > 0 || (summary?.awaiting_rows ?? 0) === 0;

  if (summaryError) {
    return (
      <div className="rounded-md bg-red-50 border border-red-200 p-4 text-sm text-red-700">
        Could not load attendance data:{" "}
        {summaryError instanceof Error ? summaryError.message : "unknown error"}
        .
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary cards (authoritative backend totals) */}
      {summaryLoading || !summary || !canonical ? (
        <div className="py-6">
          <LoadingSpinner />
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-3">
          <StatCard
            label="Advance registrations"
            value={summary.registered_rows}
            sub={`${summary.registered_headcount} people`}
          />
          {/* THE canonical "Attended" number, identical to the Overview funnel and the
              Attendees tab: advance check-ins + walk-ins (utils/eventAttendance). The two
              halves stay spelled out beneath it so neither is hidden inside the total. */}
          <StatCard
            label="Attended"
            value={canonical.attendedTotal}
            sub={`${canonical.attendedAdvance} registered + ${canonical.attendedWalkIn} walk-in`}
          />
          <StatCard
            label="Checked in"
            value={summary.checked_in_rows}
            sub={`${summary.attended_headcount} verified present`}
          />
          <StatCard
            label="Walk-ins"
            value={summary.walk_in_rows}
            sub={`${summary.walk_in_headcount} people`}
          />
          {cutoffPassed ? (
            <StatCard
              label="No-shows"
              value={summary.no_show_rows}
              sub={`${summary.no_show_headcount} people`}
            />
          ) : (
            <StatCard
              label="Awaiting check-in"
              value={summary.awaiting_rows}
              sub={`${summary.awaiting_headcount} people`}
            />
          )}
          <StatCard
            label="Attendance rate"
            value={
              canonical.advanceShowRate === null
                ? "—"
                : `${Math.round(canonical.advanceShowRate * 100)}%`
            }
            sub="advance only — walk-ins excluded both sides"
          />
          <StatCard
            label="Cancelled"
            value={summary.cancelled_rows}
            sub={
              summary.incomplete_rows > 0
                ? `${summary.incomplete_rows} evidence incomplete`
                : undefined
            }
          />
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1">
          {COHORT_TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setCohort(t.key)}
              className={`px-3 py-1.5 text-sm rounded-md border ${
                cohort === t.key
                  ? "bg-primary-600 text-white border-primary-600"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          {cutoffPassed && noShowCount > 0 && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowMaterialize(true)}
              title="Add no-show contacts to a static follow-up segment for Campaigns"
            >
              {materializeResult
                ? "Refresh follow-up segment"
                : "Create follow-up segment"}
            </Button>
          )}
          <Button
            variant="primary"
            size="sm"
            onClick={handleDownload}
            disabled={downloading}
          >
            {downloading ? "Preparing…" : "Download CSV"}
          </Button>
        </div>
      </div>

      {/* Follow-up segment confirmation */}
      {showMaterialize && summary && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm space-y-2">
          <div className="font-medium text-amber-900">
            Create / refresh the no-show follow-up segment?
          </div>
          <ul className="list-disc list-inside text-amber-800 space-y-0.5">
            <li>
              {summary.no_show_rows} no-show registrations (
              {summary.no_show_headcount} people).
            </li>
            <li>
              Only no-shows with a <strong>linked contact and email</strong> are
              added; unlinked or email-less records are excluded (exact counts
              shown after refresh).
            </li>
            <li>
              This creates/refreshes a <strong>static</strong> snapshot for use
              in Campaigns.
            </li>
            <li>
              <strong>Creating the segment does not send anything.</strong>
            </li>
          </ul>
          <div className="flex gap-2 pt-1">
            <Button
              variant="primary"
              size="sm"
              onClick={handleMaterialize}
              disabled={materializing}
            >
              {materializing ? "Refreshing…" : "Confirm"}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowMaterialize(false)}
              disabled={materializing}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
      {materializeResult && (
        <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800 flex flex-wrap items-center gap-x-4 gap-y-1">
          <span>
            <strong>{materializeResult.materialized_contact_count}</strong>{" "}
            contacts in the follow-up segment.
          </span>
          {materializeResult.excluded_unlinked_count > 0 && (
            <span>
              {materializeResult.excluded_unlinked_count} unlinked excluded.
            </span>
          )}
          {materializeResult.excluded_no_email_count > 0 && (
            <span>
              {materializeResult.excluded_no_email_count} without email
              excluded.
            </span>
          )}
          <Link to="/segments" className="text-primary-700 underline">
            View in Segments
          </Link>
        </div>
      )}

      {/* Roster */}
      {rosterError ? (
        <div className="rounded-md bg-red-50 border border-red-200 p-4 text-sm text-red-700">
          {rosterError}
        </div>
      ) : rosterLoading ? (
        <div className="py-6">
          <LoadingSpinner />
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-8 text-sm text-gray-500">
          No attendees in this view.
        </div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Contact</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Registered</th>
                <th className="px-3 py-2">Attended</th>
                <th className="px-3 py-2">Outcome</th>
                <th className="px-3 py-2">Registered at</th>
                <th className="px-3 py-2">Checked in</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {rows.map((r) => {
                const c = rowCounts(r);
                return (
                  <tr key={r.attendee_id} className="text-gray-700">
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="font-medium text-gray-900">
                        {`${r.first_name ?? ""} ${r.last_name ?? ""}`.trim() ||
                          "—"}
                      </div>
                      {r.current_member && (
                        <span className="text-xs text-green-700">
                          Current member
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                      <div>{r.email ?? "—"}</div>
                      {r.phone && (
                        <div className="text-xs text-gray-400">{r.phone}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap capitalize">
                      {r.attendee_type ?? "—"}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {c.registered.headcount}
                      <span className="text-xs text-gray-400">
                        {" "}
                        ({c.registered.memberHeadcount}m/
                        {c.registered.nonmemberHeadcount}n)
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {!c.attended.present ? (
                        <span className="text-gray-400">—</span>
                      ) : (
                        <>
                          {c.attended.headcount}
                          {(r.member_shortfall > 0 ||
                            r.nonmember_shortfall > 0) && (
                            <span className="text-xs text-orange-600">
                              {" "}
                              (short {r.member_shortfall}m/
                              {r.nonmember_shortfall}n)
                            </span>
                          )}
                        </>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs ${OUTCOME_CLASS[r.cohort] ?? "bg-gray-100 text-gray-600"}`}
                      >
                        {OUTCOME_LABEL[r.cohort] ?? r.cohort}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-500">
                      {fmtDateTime(r.registration_date)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-500">
                      {fmtDateTime(r.checked_in_at)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {cursor && !rosterLoading && (
        <div className="text-center">
          <Button variant="secondary" size="sm" onClick={loadMore}>
            Load more
          </Button>
        </div>
      )}
    </div>
  );
};

export default AttendanceReportView;
